import { SHIPMENT_COUNTRIES, moreStates } from '@mtntop/utils/src/constants/shipment.js';
import { serviceName, SERVICE_ACTIONS, SERVICE_NAME_REQUEST } from '@mtntop/utils/shared/constants/promostandards';
import { CLOSED_ORDER_STATUSES } from '@mtntop/utils/shared/constants/order/statuses';
import provinces from 'provinces';
import { FONT_AWESOME_LIBRARIES } from '@mtntop/utils/shared/constants/store/fontAwesome';

export * from './intro';
export * from './errors';

export const BOOTSTRAP_CDN = 'https://cdn.jsdelivr.net/npm/bootstrap@4.6.2/dist/css/bootstrap.min.css';

export { FONT_AWESOME_LIBRARIES };

const validateZip = (regStr, value) => {
  const regular = new RegExp(regStr);
  if (!regular.test(value)) {
    return 'Postal code for this country is not valid';
  }
  return '';
};

const mapToArray = (map) => Object.keys(map).map((key) => map[key]);

export const orderStatuses = () => [
  'Pending Payment',
  'Delivered',
  'Collections',
  'Completed',
  'Ready for Production',
  'Payment Processed',
  'In Production',
  'Shipped',
  'Sent to Supplier',
  'Pending Proof Approval',
  'Quote Sent',
  'Presentation',
  'Request Hold',
  'New Order',
  'New Quote Request',
  'New Proof Request',
  'Cart',
  'Sample Cart',
  'Proof Cart',
  'Quote Cart',
  'Box Cart',
  'Quote Paid',
  'Canceled',
  'Proof In Process',
  'On Supplier Hold',
  'New Sample Request',
  'Deleted',
  'Archive',
  'Transferred',
  'Sent to Fulfillment',
  'Ready for Fulfillment',

  'Pending Production',
  'Proof On Hold',

  'Invoice Sent',
  'New Box Request',
  'Pending Bank Verification',
  'Ready for Pickup'
];
export const ORDER_FLAG_OPTIONS = [
  { text: 'Firm Due Date', value: 'firmDueDate' },
  { text: 'Rush', value: 'rushOrder' },
  { text: 'Flagged', value: 'isFlagged' }
];
export const ORDER_FLAG_LABELS = Object.fromEntries(ORDER_FLAG_OPTIONS.map(({ text, value }) => [value, text]));
export const openOrderStatuses = () => orderStatuses().filter(s => !CLOSED_ORDER_STATUSES.includes(s));
export const orderInitialTypes = [
  'New Quote Request',
  'New Proof Request',
  'New Sample',
  'New Configured',
  'New Box Request',
  'Cart'
];

export const orderTypes = () => ['New', 'Sample', 'Decorated', 'Blank', 'Mixed'];

export const logCategories = [
  'INVOICE',
  'RECEIPT',
  'ORDER_UPDATES',
  'ORDER_PURCHASE_UPDATES',
  'ORDER_PROOF_REQUEST_UPDATES',
  'ORDER_PAYMENT',
  'EMAILS',
  'SLACK_NOTIFICATION',
  'CUSTOMER_UPDATES',
  'SSO_ERRORS',
  'ORDER_SALES_ASSIGN',
  'ORDER_ADMIN_ASSIGN',
  'CUSTOMER_SALES_ASSIGN',
  'CUSTOMER_ADMIN_ASSIGN',
  'ORDER_ESTIMATE_SHIPPING',
  'ORDER_DISCOUNT',
  'UPDATE',
  'TRANSACTIONAL_EMAIL',
  'CUSTOMER_REPORT_BUILD',
  'SEND_EMAIL',
  'PRIVATE_DATA_RESPONSE',
  'CUSTOMER_SSO_LOG_IN',
  'FROALA_IMAGE_UPLOAD_ERRORS',
  'PRODUCT_RE_CALC_PRICE',
  'QUOTE_SENT',
  'ESTIMATE_SHIPPING_ERRORS',
  'ESTIMATE_TAX_ERRORS',
  'RECAPTCHA_ERRORS',
  'PAYMENT_ERRORS',
  'XERO_ERRORS',
  'QUICKBOOKS_ERRORS',
  'MODEL_CREATE',
  'MODEL_UPDATE',
  'MODEL_REMOVED',
  'CREATE_ORDER_ERRORS',
  'UPDATE_ORDER_ERRORS',
  'REFRESH_AUTH_TOKENS',
  'ZOHO_ERRORS',
  'EMAIL_NOTIFICATION',
  'ORDER_TRIGGER'
];

export const artworkDocumentTypes = () => [
  'Production Ready',
  'Virtual Proof',
  'Supplier Art Template',
  'Designer',
  'Non Production Ready',
  'Logo'
];

export const artworkDocumentTypesDescriptions = () => ({
  'Production Ready': 'Artwork that is ready to be used in production and does not need to be converted or vectorized.',
  'Virtual Proof': 'A virtual proof of the product that is not production ready.',
  'Supplier Art Template': 'Art template provided by the supplier.',
  Designer: 'Image created using the designer.',
  'Non Production Ready': 'Customer artwork that is not production ready.'
});

export const artworkStatuses = () => ['Approved', 'Declined', 'Pending Response'];

export const customerArtworkStatuses = () => ['Approved', 'Not Approved', 'Pending Response'];

export const addressType = () => ({
  distSupplier: ['Billing', 'Shipping'],
  customers: ['Business', 'Residential']
});

export const contactType = () => [
  'Art',
  'Billing',
  'IT',
  'Sales',
  'Orders',
  'Samples',
  'Customer Service',
  'CSR',
  'Expeditor',
  'Administration',
  'Owner',
  'VP',
  'Accounting',
  'InventoryManager',
  'Shipping',
  'Warehouse',
  'AccountRepresentative',
  'Integration',
  'PromoStandards',
  'Support',
];

export const contactDepartment = () => [
  'CustomerService',
  'Administration',
  'Executive',
  'Finance',
  'Inventory',
  'Production',
  'Sales',
  'IT',
];

export const creditStatus = () => ['Good Standing', 'Delinquent', 'None'];

export const noteType = () => ['Pricing', 'Billing', 'Shipping', 'General'];

export const roles = () => ['admin', 'user'];

export const serviceCode = () => ['ORDSTAT', 'INV', 'OSN', 'Product', 'PPC', 'MED', 'INVC', 'PO', 'SPCC', 'CD'];

export { serviceName, SERVICE_ACTIONS, SERVICE_NAME_REQUEST };

const defaultPOTypes = ['Blank', 'Sample', 'Configured'];

export const poTypes = () => [...defaultPOTypes];

export const purchaseOrderFormPOTypes = () => defaultPOTypes;

export const ORDER_PAYMENT_TYPES = {
  CUSTOMER_PURCHASE_ORDER: 'Customer Purchase Order',
  CUSTOMER_CHECK: 'Customer Check',
  CREDIT_CARD: 'Credit Card',
  ECHECK: 'ECheck',
  ACH: 'ACH'
};
export const ORDER_PAYMENT_TYPES_LIST = mapToArray(ORDER_PAYMENT_TYPES);

export const SKIP_ORDER_APPROVAL_PAYMENT_TYPES = [
  'Credit Card',
  'ACH',
  'ECheck',
  'Customer Check',
  'Credit',
  'Allowance'
];

export const ORDER_DOCUMENT_PAYMENT_TYPES = [
  ORDER_PAYMENT_TYPES.CUSTOMER_PURCHASE_ORDER,
  ORDER_PAYMENT_TYPES.CUSTOMER_CHECK
];

export const TAB_TYPES = {
  CREDIT_CARD: 'Credit Card',
  PO: 'PO',
  CHECK: 'Check',
  ECHECK: 'ECheck',
  ALLOWANCE: 'Allowance',
  CREDIT: 'Credit'
};

export const ORDER_PAYMENT_TABS = [
  {
    name: TAB_TYPES.CREDIT_CARD,
    tab: TAB_TYPES.CREDIT_CARD,
    icon: ''
  },
  {
    name: TAB_TYPES.PO,
    tab: TAB_TYPES.PO,
    icon: ''
  },
  {
    name: TAB_TYPES.CHECK,
    tab: TAB_TYPES.CHECK,
    icon: ''
  },
  {
    name: TAB_TYPES.ECHECK,
    tab: TAB_TYPES.ECHECK,
    icon: ''
  },
  {
    name: TAB_TYPES.ALLOWANCE,
    tab: TAB_TYPES.ALLOWANCE,
    icon: ''
  },
  {
    name: TAB_TYPES.CREDIT,
    tab: TAB_TYPES.CREDIT,
    icon: ''
  }
];

export const poStates = [
  'Sent to Fulfillment',
  'Ready for Fulfillment',
  'New',
  'New Sample',
  'New Quote',
  'New Box',
  'New Proof',
  'New Configured',
  'Proof Pending',
  'Proof Requested',
  'Proof In Process',
  'Proof Completed',
  'Proof Sent',
  'Proof Approved',
  'Proof Being Produced',
  'Change Requested',
  'Ready for Production',
  'Product Ordered',
  'Awaiting Inventory',
  'On Supplier Hold',
  'Canceled',
  'Sent to Supplier',
  'Payment Processed',
  'Shipped',
  'Delivered',
  'In Production',
  'Request Hold',
  'Proof on Hold',
  'On Hold',
  'Transferred',
  'Archive'
];

export const lineItemToleranceDetails = ['AllowOverrunOrUnderrun', 'AllowOverRun', 'AllowUnderrun', 'ExactOnly'];

export const lineItemTypes = ['New', 'Repeat', 'Reference'];
export const storeCountries = () => {
  const usStates = [...moreStates, ...provinces.filter((province) => province.country === 'US')];
  const caStates = provinces.filter((province) => province.country === 'CA');
  const mxStates = provinces.filter((province) => province.country === 'MX');
  return {
    ...Object.keys(SHIPMENT_COUNTRIES)
      .map((e) => ({
        [SHIPMENT_COUNTRIES[e]]: {
          countryPhoneCode: '+1',
          ISO: SHIPMENT_COUNTRIES[e],
          countryName: e,
          states: []
        }
      }))
      .reduce((a, b) => ({ ...a, ...b }), {}),
    USA: {
      countryPhoneCode: '+1',
      ISO: 'US',
      countryName: 'USA',
      states: usStates.map((state) => ({
        stateName: state.name,
        stateAbbr: state.short
      }))
    },
    Canada: {
      countryPhoneCode: '+1',
      ISO: 'CA',
      countryName: 'Canada',
      states: caStates.map((state) => ({
        stateName: state.name,
        stateAbbr: state.short
      }))
    },
    Mexico: {
      countryPhoneCode: '+52',
      ISO: 'MX',
      countryName: 'Mexico',
      states: mxStates.map((state) => ({
        stateName: state.name,
        stateAbbr: state.short
      }))
    }
  };
};

export const storeCountriesStates = () => {
  const countries = storeCountries();
  return ['USA', 'Canada'].reduce(
    (list, country) => [
      ...countries[country].states.map((state) => ({
        val: state.stateName,
        label: `${state.stateName}. ${country}`
      })),
      ...list
    ],
    []
  );
};

export const zipValidators = () => [
  {
    country: 'Canada',
    mask: 'A#A #A#'
  },
  {
    country: 'USA',
    mask: '#####'
  }
];

export const currencies = [
  { code: 'USD', sign: '$' },
  { code: 'EUR', sign: '€' },
  { code: 'GBP', sign: '£' },
  { code: 'CAD', sign: '$' }
];

export const NEW_DISTRIBUTOR = 'New Distributor';
export const CANCELED_DISTRIBUTOR = 'Canceled Distributor';

export const ADDITIONAL_CHARGE_TYPES = {
  'Payment Processing Fee': 1,
  'Late Fees': 2,
  MPower: 3,
  Other: 4,
  Shipping: 5,
  Fundraiser: 6
};

const TYPES = {
  FLAT_DOLLAR_AMOUNT: 'Flat Dollar Amount',
  FLAT_PERCENTAGE: 'Flat Percentage',
  ALLOWANCE: 'Allowance',
  CREDIT: 'Credit',
  RESTRICTION_CODE: 'Restriction Code',
  SPECIALS: 'Specials',
  REDEMPTIONS: 'Redemptions'
};

const BY_CONDITIONS = {
  BY_CUSTOMER: 'byCustomer',
  BY_STORE_ROLES: 'byStoreRoles',
  BY_SUPPLIER: 'bySupplier',
  BY_FACET: 'byFacet',
  BY_CATEGORY: 'byCategory',
  BY_PRODUCT: 'byProduct'
};

const TYPE_LABELS = {
  [TYPES.FLAT_DOLLAR_AMOUNT]: 'Discount Code',
  [TYPES.FLAT_PERCENTAGE]: 'Discount Code',
  [TYPES.ALLOWANCE]: 'Allowance Code',
  [TYPES.CREDIT]: 'Credit Code',
  [TYPES.RESTRICTION_CODE]: 'Restriction Code',
  [TYPES.REDEMPTIONS]: 'Redemption Code',
  [TYPES.SPECIALS]: 'Specials Code',
  [TYPES.FREE_SHIPPING]: 'Free Shipping Code'
};

const SETTINGS = {
  AMOUNT: 'amount',
  TITLE: 'title',
  CODE: 'code',
  DESCRIPTION: 'description',
  LINK: 'link',
  IMAGE: 'image',
  ORDERS_AMOUNT: 'ordersAmount',
  APPLIES_TO: 'appliesTo',
  MULTIPLE_USE: 'multipleUse',
  MULTIPLE_CUSTOMER_USE: 'multipleCustomerUse',
  DURATION: 'duration',
  MIN_ORDER_TOTAL: 'minOrderTotal',
  BY_CONDITIONS: 'byConditions'
};

export const STORE_DISCOUNT = {
  TYPES,
  BY_CONDITIONS,
  SETTINGS,
  TYPE_LABELS
};

export const AI_IMAGE_EDIT_MODE = {
  MOVE: 'move',
  ERASE: 'erase'
};

export const AI_IMAGE_ORIENTATION_SIZES = {
  square: {
    width: 1024,
    height: 1024
  },
  landscape: {
    width: 1536,
    height: 1024
  },
  portrait: {
    width: 1024,
    height: 1536
  }
};
